import { useEffect, useMemo, useState } from 'react'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { useParams } from 'react-router-dom'
import {
  AlertCircle,
  Clock,
  Gift,
  Info,
  Loader2,
  RefreshCw,
  Settings2,
  Trophy,
  XCircle,
} from 'lucide-react'

import { AutomationTemplateMetaAlerts } from '@/components/settings/AutomationTemplateMetaAlerts'
import {
  buildWebPortalVariableMapping,
  normalizeWebPortalMappingForApi,
  normalizeWebPortalMappingFromApi,
} from '@/components/settings/webPortalTemplateMapping'
import { TemplateVariablesHelpDialog } from '@/components/actions/TemplateVariablesHelpDialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { META_INTEGRATION_ENABLED } from '@/config/env'
import { useAuth } from '@/hooks/use-auth'
import { log } from '@/lib/logger'
import { useWhatsAppStatusQuery } from '@/queries'
import { resolvePreferredWhatsAppWabaId } from '@/services/whatsappWabaUtils'
import { whatsappService, type WABA, type WhatsAppTemplate } from '@/services/whatsappService'
import {
  webPortalTemplatesService,
  type WebPortalAvailableField,
  type WebPortalSendType,
  type WebPortalVariableMappingEntry,
} from '@/services/webPortalTemplatesService'
import { risingOfferReminderService } from '@/services/risingOfferReminderService'

// The 4 send_types web-rk-iglesias triggers by calling the existing Campaign
// Engine endpoint (same backend/endpoint as tarjeta 07, different card here so
// Ofertas al Alza notifications aren't mixed with search/price alerts).
// Declarado en POSITIVO, no restando de WebPortalSendType. Antes era un Exclude
// de search_alert_match y price_drop, y cada aviso nuevo que se anadia a la
// union compartida -buyer_reactivation_check, auth_otp- entraba aqui solo y
// dejaba el Record de abajo exigiendo claves que esta tarjeta nunca rellena.
// Cada tarjeta gestiona lo suyo y lo dice; Extract mantiene la comprobacion de
// que esas cuatro existen de verdad en el tipo compartido.
type PortalSendType = Extract<
  WebPortalSendType,
  'rising_offer_received' | 'rising_offer_updated' | 'rising_offer_winner' | 'rising_offer_not_winner'
>

type PortalSendTypeMeta = {
  send_type: PortalSendType
  title: string
  description: string
  icon: typeof Gift
  expected: number
}

const PORTAL_SEND_TYPES: PortalSendTypeMeta[] = [
  {
    send_type: 'rising_offer_received',
    title: 'Oferta recibida',
    description: 'Aviso al oferente cuando presenta su primera oferta sobre una propiedad.',
    icon: Gift,
    expected: 2,
  },
  {
    send_type: 'rising_offer_updated',
    title: 'Oferta actualizada',
    description: 'Aviso al oferente cuando mejora una oferta ya presentada.',
    icon: RefreshCw,
    expected: 2,
  },
  {
    send_type: 'rising_offer_winner',
    title: 'Oferta ganadora',
    description: 'Aviso al oferente ganador cuando el gestor elige su oferta.',
    icon: Trophy,
    expected: 1,
  },
  {
    send_type: 'rising_offer_not_winner',
    title: 'Oferta no ganadora',
    description: 'Aviso a cada oferente perdedor cuando se elige otra oferta.',
    icon: XCircle,
    expected: 1,
  },
]

const HELP_VARIABLES = [
  { slot: 'Recibida/Actualizada · {{1}}', label: 'Título de la propiedad', source: 'Portal → propiedad' },
  { slot: 'Recibida/Actualizada · {{2}}', label: 'Precio ofertado', source: 'Portal → oferta' },
  { slot: 'Ganadora/No ganadora · {{1}}', label: 'Título de la propiedad', source: 'Portal → propiedad' },
  { slot: 'Recordatorio · {{1}}', label: 'Título de la propiedad', source: 'Visita agendada' },
  { slot: 'Botón', label: 'Ver propiedad', source: 'URL a la ficha de la propiedad' },
]

function countTemplateBodyVariables(template: WhatsAppTemplate): number {
  const body = template.components.find((component) => component.type?.toUpperCase() === 'BODY')
  if (!body?.text) return 0
  const matches = body.text.match(/\{\{\d+\}\}/g)
  return matches ? matches.length : 0
}

type PortalRowState = {
  template_name: string
  template_language: string
  waba_id: string
  enabled: boolean
  variable_mapping: WebPortalVariableMappingEntry[]
  available_fields: WebPortalAvailableField[]
  toggling: boolean
}

const emptyPortalRow = (): PortalRowState => ({
  template_name: '',
  template_language: 'es_ES',
  waba_id: '',
  enabled: false,
  variable_mapping: [],
  available_fields: [],
  toggling: false,
})

type ReminderRowState = {
  offset_minutes: number
  template_name: string
  template_language: string
  enabled: boolean
  variable_mapping: WebPortalVariableMappingEntry[]
  toggling: boolean
}

const emptyReminderRow = (): ReminderRowState => ({
  offset_minutes: 120,
  template_name: '',
  template_language: 'es_ES',
  enabled: false,
  variable_mapping: [],
  toggling: false,
})

const REMINDER_EXPECTED = 1
const REMINDER_AVAILABLE_FIELDS: WebPortalAvailableField[] = [
  { key: 'property_title', label: 'Título de la propiedad' },
]

type ActiveRow = PortalSendType | 'rising_offer_reminder' | null

type DialogDraft = {
  waba_id: string
  template_key: string
  variable_mapping: WebPortalVariableMappingEntry[]
  offset_minutes: number
}

const EMPTY_DRAFT: DialogDraft = { waba_id: '', template_key: '', variable_mapping: [], offset_minutes: 120 }

export function RisingOffersNotificationsCard() {
  const { lang = 'es' } = useParams()
  const { token } = useAuth()
  const hasAuthToken = Boolean(token && token.trim())
  const [isOpen, setIsOpen] = useState(false)

  const { data: whatsappStatus, isLoading: whatsappStatusLoading } = useWhatsAppStatusQuery({
    enabled: META_INTEGRATION_ENABLED && hasAuthToken,
  })
  const whatsappConnected = Boolean(whatsappStatus?.connected)
  const canConfigureTemplates = META_INTEGRATION_ENABLED && whatsappConnected

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const [portalRows, setPortalRows] = useState<Record<PortalSendType, PortalRowState>>({
    rising_offer_received: emptyPortalRow(),
    rising_offer_updated: emptyPortalRow(),
    rising_offer_winner: emptyPortalRow(),
    rising_offer_not_winner: emptyPortalRow(),
  })
  const [reminderRow, setReminderRow] = useState<ReminderRowState>(emptyReminderRow())

  const [activeRow, setActiveRow] = useState<ActiveRow>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState<DialogDraft>(EMPTY_DRAFT)
  const [wabas, setWabas] = useState<WABA[]>([])
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])

  const activePortalMeta = useMemo(
    () => PORTAL_SEND_TYPES.find((item) => item.send_type === activeRow) ?? null,
    [activeRow],
  )
  const isReminderActive = activeRow === 'rising_offer_reminder'
  const activeExpected = isReminderActive ? REMINDER_EXPECTED : activePortalMeta?.expected ?? 0
  const activeAvailableFields = isReminderActive
    ? REMINDER_AVAILABLE_FIELDS
    : activeRow
      ? portalRows[activeRow].available_fields
      : []

  const loadConfig = async () => {
    if (!hasAuthToken) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [portalResponse, reminderSettings] = await Promise.all([
        webPortalTemplatesService.getConfig(),
        risingOfferReminderService.getSettings().catch((err) => {
          log.error('Rising offer reminder settings load failed', {
            message: err instanceof Error ? err.message : String(err),
          })
          return null
        }),
      ])

      setPortalRows((prev) => {
        const next = { ...prev }
        for (const cfg of portalResponse.data || []) {
          if (cfg.send_type in next) {
            const meta = PORTAL_SEND_TYPES.find((item) => item.send_type === cfg.send_type)
            next[cfg.send_type as PortalSendType] = {
              ...next[cfg.send_type as PortalSendType],
              template_name: cfg.template_name,
              template_language: cfg.template_language || 'es_ES',
              waba_id: cfg.waba_id,
              enabled: cfg.enabled,
              variable_mapping: buildWebPortalVariableMapping(
                meta?.expected ?? 0,
                normalizeWebPortalMappingFromApi(cfg.variable_mapping),
              ),
              available_fields: cfg.available_fields || [],
            }
          }
        }
        return next
      })

      if (reminderSettings) {
        setReminderRow({
          offset_minutes: reminderSettings.offset_minutes,
          template_name: reminderSettings.template_name,
          template_language: reminderSettings.template_language || 'es_ES',
          enabled: reminderSettings.enabled,
          variable_mapping: buildWebPortalVariableMapping(
            REMINDER_EXPECTED,
            normalizeWebPortalMappingFromApi(reminderSettings.variable_mapping),
          ),
          toggling: false,
        })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo cargar la configuración'
      log.error('Rising offers notifications config load failed', { message })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAuthToken, token])

  const loadTemplatesForWaba = async (wabaId: string) => {
    setTemplates([])
    try {
      const data = await whatsappService.getTemplates(wabaId)
      setTemplates(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las plantillas'
      log.error('Rising offers notifications template load failed', { message, wabaId })
      setDialogError(message)
    }
  }

  const openPortalDialog = async (sendType: PortalSendType) => {
    const meta = PORTAL_SEND_TYPES.find((item) => item.send_type === sendType)
    const row = portalRows[sendType]
    setActiveRow(sendType)
    setDialogError(null)
    setDraft({
      waba_id: row.waba_id,
      template_key: row.template_name ? `${row.template_name}:${row.template_language}` : '',
      variable_mapping: row.variable_mapping,
      offset_minutes: 120,
    })
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      const preferredWabaId = (row.waba_id && availableWabas.some((item) => item.id === row.waba_id)
        ? row.waba_id
        : availableWabas[0]?.id) || ''
      setDraft((prev) => ({ ...prev, waba_id: preferredWabaId }))
      if (!availableWabas.length) {
        setDialogError('No hay ninguna cuenta de WhatsApp conectada.')
      }
      if (preferredWabaId) {
        const loadedTemplates = await whatsappService.getTemplates(preferredWabaId)
        setTemplates(loadedTemplates)
        if (row.template_name) {
          setDraft((prev) => ({
            ...prev,
            variable_mapping: buildWebPortalVariableMapping(meta?.expected ?? 0, row.variable_mapping),
          }))
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las cuentas de WhatsApp'
      log.error('Rising offers notifications WABA load failed', { message })
      setDialogError(message)
    } finally {
      setDialogLoading(false)
    }
  }

  const openReminderDialog = async () => {
    setActiveRow('rising_offer_reminder')
    setDialogError(null)
    setDraft({
      waba_id: '',
      template_key: reminderRow.template_name
        ? `${reminderRow.template_name}:${reminderRow.template_language}`
        : '',
      variable_mapping: reminderRow.variable_mapping,
      offset_minutes: reminderRow.offset_minutes,
    })
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      const preferredWabaId = resolvePreferredWhatsAppWabaId(availableWabas, whatsappStatus?.account_id)
      setDraft((prev) => ({ ...prev, waba_id: preferredWabaId }))
      if (!preferredWabaId) {
        setDialogError('No hay ninguna cuenta de WhatsApp conectada.')
        return
      }
      const loadedTemplates = await whatsappService.getTemplates(preferredWabaId)
      setTemplates(loadedTemplates)
      if (reminderRow.template_name) {
        setDraft((prev) => ({
          ...prev,
          variable_mapping: buildWebPortalVariableMapping(REMINDER_EXPECTED, reminderRow.variable_mapping),
        }))
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las cuentas de WhatsApp'
      log.error('Rising offer reminder WABA load failed', { message })
      setDialogError(message)
    } finally {
      setDialogLoading(false)
    }
  }

  const handleWabaChange = async (value: string) => {
    setDraft((prev) => ({ ...prev, waba_id: value, template_key: '', variable_mapping: [] }))
    setDialogError(null)
    await loadTemplatesForWaba(value)
  }

  const handleTemplateChange = (templateKey: string) => {
    setDraft((prev) => ({
      ...prev,
      template_key: templateKey,
      variable_mapping: buildWebPortalVariableMapping(activeExpected, prev.variable_mapping),
    }))
  }

  const handleMappingChange = (position: number, sourceKey: string) => {
    setDraft((prev) => ({
      ...prev,
      variable_mapping: prev.variable_mapping.map((item) =>
        item.position === position ? { ...item, source_type: 'variable', source_key: sourceKey } : item,
      ),
    }))
  }

  const handleSaveDialog = async () => {
    if (!activeRow) return
    const selectedWaba = wabas.find((item) => item.id === draft.waba_id)
    const selectedTemplate = templates.find((item) => `${item.name}:${item.language}` === draft.template_key)
    if (!selectedWaba || !selectedTemplate) {
      setDialogError('Selecciona una cuenta de WhatsApp y una plantilla.')
      return
    }
    const count = countTemplateBodyVariables(selectedTemplate)
    if (count !== activeExpected) {
      setDialogError(`La plantilla tiene ${count} variables; este aviso requiere exactamente ${activeExpected}.`)
      return
    }
    if (activeExpected > 0 && draft.variable_mapping.some((item) => !item.source_key)) {
      setDialogError('Mapea cada variable de la plantilla a un dato disponible.')
      return
    }

    setSaving(true)
    setDialogError(null)
    try {
      if (isReminderActive) {
        const saved = await risingOfferReminderService.updateSettings({
          enabled: reminderRow.enabled,
          offset_minutes: draft.offset_minutes,
          template_name: selectedTemplate.name,
          template_language: selectedTemplate.language,
          body_variable_count: activeExpected,
          variable_mapping: normalizeWebPortalMappingForApi(draft.variable_mapping),
        })
        setReminderRow({
          offset_minutes: saved.offset_minutes,
          template_name: saved.template_name,
          template_language: saved.template_language || 'es_ES',
          enabled: saved.enabled,
          variable_mapping: buildWebPortalVariableMapping(
            REMINDER_EXPECTED,
            normalizeWebPortalMappingFromApi(saved.variable_mapping),
          ),
          toggling: false,
        })
      } else {
        const sendType = activeRow as PortalSendType
        const response = await webPortalTemplatesService.putConfig({
          send_type: sendType,
          template_name: selectedTemplate.name,
          template_language: selectedTemplate.language,
          waba_id: selectedWaba.id,
          enabled: portalRows[sendType].enabled,
          variable_mapping: normalizeWebPortalMappingForApi(draft.variable_mapping),
        })
        const saved = response.data
        setPortalRows((prev) => ({
          ...prev,
          [sendType]: {
            ...prev[sendType],
            template_name: saved.template_name,
            template_language: saved.template_language || 'es_ES',
            waba_id: saved.waba_id,
            enabled: saved.enabled,
            variable_mapping: buildWebPortalVariableMapping(
              activeExpected,
              normalizeWebPortalMappingFromApi(saved.variable_mapping),
            ),
            available_fields: saved.available_fields || prev[sendType].available_fields,
          },
        }))
      }
      setDialogOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar'
      log.error('Rising offers notifications save failed', { message, sendType: activeRow })
      setDialogError(message)
    } finally {
      setSaving(false)
    }
  }

  const handlePortalToggle = async (sendType: PortalSendType, enabled: boolean) => {
    const row = portalRows[sendType]
    if (!row.template_name) return
    setPortalRows((prev) => ({ ...prev, [sendType]: { ...prev[sendType], toggling: true } }))
    setError(null)
    try {
      const response = await webPortalTemplatesService.putConfig({
        send_type: sendType,
        template_name: row.template_name,
        template_language: row.template_language,
        waba_id: row.waba_id,
        enabled,
        variable_mapping: normalizeWebPortalMappingForApi(row.variable_mapping),
      })
      const saved = response.data
      setPortalRows((prev) => ({
        ...prev,
        [sendType]: { ...prev[sendType], enabled: saved.enabled, toggling: false },
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar el estado'
      log.error('Rising offers notifications toggle failed', { message, sendType })
      setError(message)
      setPortalRows((prev) => ({ ...prev, [sendType]: { ...prev[sendType], toggling: false } }))
    }
  }

  const handleReminderToggle = async (enabled: boolean) => {
    if (!reminderRow.template_name) return
    setReminderRow((prev) => ({ ...prev, toggling: true }))
    setError(null)
    try {
      const saved = await risingOfferReminderService.updateSettings({
        enabled,
        offset_minutes: reminderRow.offset_minutes,
        template_name: reminderRow.template_name,
        template_language: reminderRow.template_language,
        body_variable_count: REMINDER_EXPECTED,
        variable_mapping: normalizeWebPortalMappingForApi(reminderRow.variable_mapping),
      })
      setReminderRow((prev) => ({ ...prev, enabled: saved.enabled, toggling: false }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar el estado'
      log.error('Rising offer reminder toggle failed', { message })
      setError(message)
      setReminderRow((prev) => ({ ...prev, toggling: false }))
    }
  }

  const anyEnabled = useMemo(
    () => PORTAL_SEND_TYPES.some((meta) => portalRows[meta.send_type].enabled) || reminderRow.enabled,
    [portalRows, reminderRow.enabled],
  )

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando notificaciones de Ofertas al Alza...
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <SeccionPlegable
        numero="OA"
        etiqueta="Ofertas al Alza"
        titulo="Notificaciones de Ofertas al Alza"
        descripcion="Plantilla y estado para las 5 plantillas de Ofertas al Alza (portal web + recordatorio
                  post-visita)."
        estado={
          <>
{!canConfigureTemplates ? (
                    <Badge variant="outline" className="ml-1 border-warning/40 text-warning">
                      Vista local
                    </Badge>
                  ) : anyEnabled ? (
                    <Badge variant="outline" className="ml-1 border-success/40 text-success">
                      Activa
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="ml-1">
                      Pausada
                    </Badge>
                  )}
          </>
        }
        abierta={isOpen}
        onAlternar={() => setIsOpen((prev) => !prev)}
        acciones={
          <>
<Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="shrink-0 rounded-full text-muted-foreground"
              aria-label="Ayuda variables notificaciones de Ofertas al Alza"
              onClick={() => setHelpOpen(true)}
            >
              <Info className="h-4 w-4" />
            </Button>
          </>
        }
      >
              <AutomationTemplateMetaAlerts
                lang={lang}
                metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                whatsappConnected={whatsappConnected}
                whatsappStatusLoading={whatsappStatusLoading}
              />

              {error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Notificación</TableHead>
                      <TableHead>Plantilla</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {PORTAL_SEND_TYPES.map((meta) => {
                      const row = portalRows[meta.send_type]
                      const RowIcon = meta.icon
                      return (
                        <TableRow key={meta.send_type}>
                          <TableCell>
                            <div className="flex items-center gap-2 font-medium text-foreground">
                              <RowIcon className="h-4 w-4 text-primary" />
                              {meta.title}
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground">{meta.description}</p>
                          </TableCell>
                          <TableCell>
                            {row.template_name ? (
                              <>
                                <div className="font-medium">{row.template_name}</div>
                                <div className="text-xs text-muted-foreground">{row.template_language}</div>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Sin configurar</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={row.enabled}
                                disabled={!canConfigureTemplates || !row.template_name || row.toggling}
                                onCheckedChange={(checked) => void handlePortalToggle(meta.send_type, checked)}
                              />
                              <span className="text-xs text-muted-foreground">
                                {row.enabled ? 'Activo' : 'Pausado'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!canConfigureTemplates}
                              onClick={() => void openPortalDialog(meta.send_type)}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow key="rising_offer_reminder">
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium text-foreground">
                          <Clock className="h-4 w-4 text-primary" />
                          Recordatorio post-visita
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Se dispara {reminderRow.offset_minutes} min. después de una visita ya pasada sobre una
                          propiedad con Ofertas al Alza activo — tiempo propio, independiente del recordatorio
                          post-visita genérico.
                        </p>
                      </TableCell>
                      <TableCell>
                        {reminderRow.template_name ? (
                          <>
                            <div className="font-medium">{reminderRow.template_name}</div>
                            <div className="text-xs text-muted-foreground">{reminderRow.template_language}</div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sin configurar</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={reminderRow.enabled}
                            disabled={!canConfigureTemplates || !reminderRow.template_name || reminderRow.toggling}
                            onCheckedChange={(checked) => void handleReminderToggle(checked)}
                          />
                          <span className="text-xs text-muted-foreground">
                            {reminderRow.enabled ? 'Activo' : 'Pausado'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!canConfigureTemplates}
                          onClick={() => void openReminderDialog()}
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
      </SeccionPlegable>

      <TemplateVariablesHelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        title="Variables de las notificaciones de Ofertas al Alza"
        description="Cada aviso construye sus variables con un orden fijo; aquí solo eliges qué plantilla aprobada las recibe, si está activa, y (para el recordatorio) cuándo se dispara."
        orderNote="El teléfono del destinatario no es una variable de plantilla: se usa como destinatario del WhatsApp."
        variablesTitle="Variables por aviso"
        sourceLabel="Origen:"
        variables={HELP_VARIABLES}
        examples={[
          { title: 'Oferta recibida', text: '¡Oferta recibida! «{{1}}» por {{2}}.' },
          { title: 'Oferta ganadora', text: '¡Felicidades! Tu oferta por «{{1}}» fue la elegida.' },
          { title: 'Recordatorio post-visita', text: '¿Quieres presentar una oferta por «{{1}}»?' },
        ]}
        note="El botón siempre enlaza a la ficha pública de la propiedad."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isReminderActive ? 'Recordatorio post-visita' : activePortalMeta?.title ?? 'Configurar plantilla'}
            </DialogTitle>
          </DialogHeader>

          {dialogLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando plantillas de Meta...
            </div>
          ) : (
            <div className="space-y-4">
              {dialogError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{dialogError}</AlertDescription>
                </Alert>
              ) : null}

              {isReminderActive ? (
                <div className="space-y-2">
                  <Label>Minutos después de la visita</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60 * 24 * 30}
                    value={draft.offset_minutes}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, offset_minutes: Number(event.target.value) || 1 }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Independiente del offset de Postvisita genérico (Previsita/Postvisita) — este es su propio
                    tiempo.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Cuenta de WhatsApp</Label>
                  <Select value={draft.waba_id || undefined} onValueChange={(value) => void handleWabaChange(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar WABA" />
                    </SelectTrigger>
                    <SelectContent>
                      {wabas.map((waba) => (
                        <SelectItem key={waba.id} value={waba.id}>
                          {waba.name || waba.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Plantilla aprobada de Meta</Label>
                <Select
                  value={draft.template_key || undefined}
                  onValueChange={handleTemplateChange}
                  disabled={(!isReminderActive && !draft.waba_id) || templates.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem
                        key={`${template.name}:${template.language}`}
                        value={`${template.name}:${template.language}`}
                      >
                        {template.name} ({template.language}) — {countTemplateBodyVariables(template)} vars
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {draft.variable_mapping.length > 0 ? (
                <div className="space-y-2">
                  <Label>Mapa de variables</Label>
                  <p className="text-xs text-muted-foreground">
                    Asigna cada hueco de la plantilla a un dato disponible.
                  </p>
                  {draft.variable_mapping.map((mapping) => (
                    <div key={mapping.position} className="grid grid-cols-[64px_1fr] items-center gap-2">
                      <span className="text-sm font-medium">{`{{${mapping.position}}}`}</span>
                      <Select
                        value={mapping.source_key || undefined}
                        onValueChange={(value) => handleMappingChange(mapping.position, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar dato" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeAvailableFields.map((field) => (
                            <SelectItem key={field.key} value={field.key}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    <span className="font-mono text-foreground">Botón</span> Ver propiedad (link automático)
                  </p>
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={saving || dialogLoading || !draft.template_key}
              onClick={() => void handleSaveDialog()}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
